// @file Slideshow realtime store
// - Owns the reactive state (currentPresenter, slideshowSessionSlideId, isFollowingPresenter, etc.)
// - Manages the channel lifecycle and message handling
// - Has no Vue component coupling — no onMounted, no watchers
import { getVuexStore } from '@@/bits/pinia'
import { RootState } from '@@/vuexstore/surface/types'
import type * as Phoenix from 'phoenix'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { useSurfaceStore } from './surface'
import { useSurfaceCurrentUserStore } from './surface_current_user'

type BroadcastCurrentSlideMessage = Record<string, number> // user_id => slide_id

type SpotlightTransferRequestPayload = { requester_id: string; requester_name: string; requester_avatar_url: string }

type CurrentPresenter = { id: number; name: string; avatar_url: string }

type ViewerInfo = { name: string; avatar_url: string; is_admin: boolean; slide_index: number | null }

type SlideshowChannelMessage =
  | { event: 'broadcast_current_slide'; uid?: string; message: BroadcastCurrentSlideMessage }
  | { event: 'latest_slide_broadcasted'; uid?: string; message: BroadcastCurrentSlideMessage }
  | { event: 'current_presenter'; uid?: string; message: { id: string; name: string; avatar_url: string } | null }
  | { event: 'presenter_stopped'; uid?: string; message: Record<string, never> }
  | { event: 'spotlight:transfer_requested'; uid?: string; message: SpotlightTransferRequestPayload }
  | { event: 'spotlight:transfer_denied'; uid?: string; message: Record<string, never> }
  | { event: 'spotlight:transfer_cancelled'; uid?: string; message: Record<string, never> }
  | { event: 'current_transfer_request'; uid?: string; message: SpotlightTransferRequestPayload | null }
  | { event: 'viewer:slide_changed'; uid?: string; message: { user_id: string; slide_index: number | null } }
  | { event: 'current_viewer_positions'; uid?: string; message: Record<string, number> }
  | { event: 'current_viewers'; uid?: string; message: Record<string, ViewerInfo> }
  | { event: 'viewer:joined'; uid?: string; message: ViewerInfo & { user_id: string } }
  | { event: 'viewer:left'; uid?: string; message: { user_id: string } }
  | { event: 'broadcast_current_album_image'; uid?: string; message: { post_cid: string; image_index: number } }
  | { event: 'latest_album_image_broadcasted'; uid?: string; message: { post_cid: string; image_index: number } | null }
  | { event: 'comment_panel_pinned'; uid?: string; message: { pinned: boolean; open: boolean } }
  | { event: 'latest_comment_panel_pinned'; uid?: string; message: { pinned: boolean; open: boolean } | null }

const useSlideshowRealtimeStore = defineStore('slideshowRealtime', () => {
  const surfaceStore = useSurfaceStore()
  const currentDeviceUid = computed(() => surfaceStore.uid)

  const currentUserStore = useSurfaceCurrentUserStore()
  const currentUserId = computed(() => currentUserStore.currentUser?.id)

  // ── Channel connection ───────────────────────────────────────────────────────
  const surfaceVuexStore = getVuexStore<RootState>()
  const brahmsSocket = computed(
    () => surfaceVuexStore?.state.realtime?.brahmsSocket as Phoenix.Socket | null | undefined,
  )
  const slideshowChannel = ref<Phoenix.Channel | null>(null)

  async function connectToSlideshowSession(wallId: number, initialSlideIndex: number): Promise<void> {
    if (slideshowChannel.value != null) {
      await disconnectFromSlideshowSession()
    }
    return await new Promise((resolve, _reject) => {
      slideshowChannel.value = brahmsSocket.value?.channel(`slideshow:${wallId}`) ?? null
      if (slideshowChannel.value == null) {
        resolve()
        return
      }
      slideshowChannel.value.on('new_msg', (data) => {
        handleSlideshowChannelMessage(data)
      })
      slideshowChannel.value.join().receive('ok', () => {
        pushViewerSlideUpdate(initialSlideIndex)
        resolve()
      })
    })
  }

  async function disconnectFromSlideshowSession(): Promise<void> {
    return await new Promise((resolve, _reject) => {
      slideshowSessionSlideId.value = null
      currentPresenter.value = null
      broadcastedSlidesMap.value = {}
      slideshowSessionAlbumImage.value = null
      presenterCommentPanelPinned.value = false
      presenterCommentPanelOpen.value = false
      isFollowingPresenter.value = true
      spotlightRequester.value = null
      viewers.value = {}
      viewersInitialized.value = false
      viewerInfoStale = false
      if (slideshowChannel.value == null) {
        resolve()
        return
      }
      slideshowChannel.value.leave().receive('ok', () => {
        slideshowChannel.value = null
        resolve()
      })
    })
  }

  // ── Presenter & slide tracking ─────────────────────────────────────────────
  const currentPresenter = ref<CurrentPresenter | null>(null)
  const slideshowSessionSlideId = ref<number | null>(null)
  const broadcastedSlidesMap = shallowRef<BroadcastCurrentSlideMessage>({})
  const slideshowSessionAlbumImage = ref<{ postCid: string; imageIndex: number } | null>(null)

  const isPresenter = computed(() => currentUserId.value != null && currentPresenter.value?.id === currentUserId.value)

  function setPresenterCurrentSlide(payload: BroadcastCurrentSlideMessage): void {
    if (currentPresenter.value == null) return
    slideshowSessionSlideId.value = payload[currentPresenter.value.id] ?? null
  }

  function setBroadcastedSlide(payload: BroadcastCurrentSlideMessage): void {
    broadcastedSlidesMap.value = { ...broadcastedSlidesMap.value, ...payload }
    setPresenterCurrentSlide(payload)
  }

  function pushBroadcastCurrentSlide(slideId: number): void {
    if (slideId == null) return
    if (currentPresenter.value == null) return
    if (currentPresenter.value.id !== currentUserId.value) return
    if (slideshowChannel.value == null) return
    slideshowChannel.value.push('broadcast_current_slide', { user_id: currentUserId.value, slide_id: slideId })
  }

  function pushBroadcastCurrentAlbumImage(postCid: string, imageIndex: number): void {
    if (currentPresenter.value == null) return
    if (currentPresenter.value.id !== currentUserId.value) return
    if (slideshowChannel.value == null) return
    slideshowChannel.value.push('broadcast_current_album_image', { post_cid: postCid, image_index: imageIndex })
  }

  // ── Comment panel state broadcasting ────────────────────────────────────────
  const presenterCommentPanelPinned = ref(false)
  const presenterCommentPanelOpen = ref(false)

  function pushBroadcastCommentPanelState(pinned: boolean, open: boolean): void {
    if (currentPresenter.value == null) return
    if (currentPresenter.value.id !== currentUserId.value) return
    if (slideshowChannel.value == null) return
    slideshowChannel.value.push('broadcast_comment_panel_pinned', { pinned, open })
  }

  // ── Spotlight ──────────────────────────────────────────────────────────────
  const isSpotlightActive = computed(() => currentPresenter.value != null)
  const presenterViewerCount = computed(() => Math.max(0, Object.keys(viewers.value).length - 1))
  const isCurrentUserLockedToPresenter = computed(
    () => isSpotlightActive.value && isFollowingPresenter.value && !isPresenter.value,
  )

  function startSpotlight(presenterName: string, presenterAvatarUrl: string): void {
    slideshowChannel.value?.push('start_spotlight', {
      presenter_name: presenterName,
      presenter_avatar_url: presenterAvatarUrl,
    })
  }

  function stopSpotlight(): void {
    slideshowChannel.value?.push('stop_spotlight', {})
  }

  // ── Spotlight transfer ─────────────────────────────────────────────────────
  const spotlightRequester = ref<{ id: string; name: string } | null>(null)
  const wasRequestDenied = ref(false)

  const isCurrentUserRequester = computed(
    () => currentUserId.value != null && spotlightRequester.value?.id === String(currentUserId.value),
  )
  const hasPendingTransferRequest = computed(() => spotlightRequester.value !== null)

  function requestTransfer(requesterName: string, requesterAvatarUrl: string): void {
    slideshowChannel.value?.push('spotlight_request_transfer', {
      requester_name: requesterName,
      requester_avatar_url: requesterAvatarUrl,
    })
  }

  function acceptTransfer(): void {
    slideshowChannel.value?.push('spotlight_accept_transfer', {})
  }

  function denyTransfer(): void {
    slideshowChannel.value?.push('spotlight_deny_transfer', {})
  }

  function cancelRequest(): void {
    slideshowChannel.value?.push('spotlight_cancel_request', {})
  }

  // ── Viewer info tracking ─────────────────────────────────────────────────
  const viewers = shallowRef<Record<string, ViewerInfo>>({})
  const viewersInitialized = ref(false)

  const isFollowingPresenter = ref<boolean>(true)

  function pushViewerSlideUpdate(slideIndex: number): void {
    slideshowChannel.value?.push('viewer_slide_update', { slide_index: slideIndex })
  }

  let viewerInfoStale = false

  /** Signal that the current user's info changed. The next viewer:visible push will tell the backend to re-fetch. */
  function markViewerInfoStale(): void {
    viewerInfoStale = true
  }

  function pushViewerHidden(): void {
    slideshowChannel.value?.push('viewer:hidden', {})
  }

  function pushViewerVisible(slideIndex: number): void {
    if (slideshowChannel.value == null) return
    slideshowChannel.value.push('viewer:visible', { slide_index: slideIndex, info_stale: viewerInfoStale })
    viewerInfoStale = false
  }

  function updateViewerUserInfo(userId: string, info: { name: string; avatarUrl: string }): void {
    if (viewers.value[userId] == null) return
    viewers.value = {
      ...viewers.value,
      [userId]: { ...viewers.value[userId], name: info.name, avatar_url: info.avatarUrl },
    }
  }

  // ── Message handler ────────────────────────────────────────────────────────
  function handleSlideshowChannelMessage(messageObj?: SlideshowChannelMessage): void {
    if (messageObj == null) return
    if (messageObj.uid != null && messageObj.uid === currentDeviceUid.value) return

    switch (messageObj.event) {
      // broadcast_current_slide: real-time fan-out to all clients when the presenter navigates
      // latest_slide_broadcasted: point-to-point catch-up push to a newly joined client
      case 'broadcast_current_slide':
      case 'latest_slide_broadcasted':
        setBroadcastedSlide(messageObj.message)
        break
      case 'broadcast_current_album_image':
      case 'latest_album_image_broadcasted':
        if (messageObj.message != null) {
          slideshowSessionAlbumImage.value = {
            postCid: messageObj.message.post_cid,
            imageIndex: messageObj.message.image_index,
          }
        } else {
          slideshowSessionAlbumImage.value = null
        }
        break
      case 'current_presenter':
        currentPresenter.value =
          messageObj.message != null
            ? {
                id: Number(messageObj.message.id),
                name: messageObj.message.name,
                avatar_url: messageObj.message.avatar_url ?? '',
              }
            : null
        spotlightRequester.value = null
        setPresenterCurrentSlide(broadcastedSlidesMap.value)
        // Reset pin state on transfer so stale state from the previous presenter
        // doesn't persist until the new presenter's broadcast arrives.
        presenterCommentPanelPinned.value = false
        presenterCommentPanelOpen.value = false
        break
      case 'presenter_stopped':
        currentPresenter.value = null
        slideshowSessionSlideId.value = null
        slideshowSessionAlbumImage.value = null
        presenterCommentPanelPinned.value = false
        presenterCommentPanelOpen.value = false
        isFollowingPresenter.value = true
        spotlightRequester.value = null
        break
      case 'comment_panel_pinned':
      case 'latest_comment_panel_pinned':
        presenterCommentPanelPinned.value = messageObj.message?.pinned ?? false
        presenterCommentPanelOpen.value = messageObj.message?.open ?? false
        break
      case 'spotlight:transfer_requested':
        spotlightRequester.value = { id: messageObj.message.requester_id, name: messageObj.message.requester_name }
        break
      case 'spotlight:transfer_denied':
        wasRequestDenied.value = true
        spotlightRequester.value = null
        break
      case 'spotlight:transfer_cancelled':
        spotlightRequester.value = null
        break
      case 'current_transfer_request':
        if (messageObj.message != null) {
          spotlightRequester.value = { id: messageObj.message.requester_id, name: messageObj.message.requester_name }
        } else {
          spotlightRequester.value = null
        }
        break
      case 'viewer:slide_changed': {
        const userId = messageObj.message.user_id
        if (viewers.value[userId] != null) {
          viewers.value = {
            ...viewers.value,
            [userId]: { ...viewers.value[userId], slide_index: messageObj.message.slide_index },
          }
        }
        break
      }
      case 'current_viewer_positions': {
        const positions = messageObj.message as Record<string, number>
        const updated = { ...viewers.value }
        for (const [id, slideIndex] of Object.entries(positions)) {
          updated[id] =
            updated[id] != null
              ? { ...updated[id], slide_index: slideIndex }
              : { name: '', avatar_url: '', is_admin: false, slide_index: slideIndex }
        }
        viewers.value = updated
        break
      }
      case 'current_viewers': {
        const incoming = messageObj.message as Record<string, ViewerInfo>
        const updated: Record<string, ViewerInfo> = {}
        for (const [id, viewer] of Object.entries(incoming)) {
          const existing = viewers.value[id]
          updated[id] =
            existing != null ? { ...viewer, slide_index: existing.slide_index ?? viewer.slide_index } : viewer
        }
        viewers.value = updated
        viewersInitialized.value = true
        break
      }
      case 'viewer:joined': {
        const { user_id: joinedUserId, ...viewerInfo } = messageObj.message
        viewers.value = { ...viewers.value, [joinedUserId]: viewerInfo }
        break
      }
      case 'viewer:left': {
        const { [messageObj.message.user_id]: _, ...remainingViewers } = viewers.value
        viewers.value = remainingViewers
        break
      }
    }
  }

  return {
    currentUserId,
    currentPresenter,
    slideshowSessionSlideId,
    isFollowingPresenter,
    broadcastedSlidesMap,
    slideshowSessionAlbumImage,
    spotlightRequester,
    viewers,
    viewersInitialized,
    isPresenter,
    isSpotlightActive,
    presenterViewerCount,
    isCurrentUserRequester,
    hasPendingTransferRequest,
    wasRequestDenied,
    isCurrentUserLockedToPresenter,
    connectToSlideshowSession,
    disconnectFromSlideshowSession,
    startSpotlight,
    stopSpotlight,
    requestTransfer,
    acceptTransfer,
    denyTransfer,
    cancelRequest,
    pushBroadcastCurrentSlide,
    pushBroadcastCurrentAlbumImage,
    presenterCommentPanelPinned,
    presenterCommentPanelOpen,
    pushBroadcastCommentPanelState,
    pushViewerSlideUpdate,
    pushViewerHidden,
    pushViewerVisible,
    markViewerInfoStale,
    updateViewerUserInfo,
  }
})
export { useSlideshowRealtimeStore }
export type { ViewerInfo }
